import React, { useState, useEffect } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import ja from 'date-fns/locale/ja';
import 'react-datepicker/dist/react-datepicker.css';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import DataTable from './DataTable';
import './EnergyGraph.css';

registerLocale('ja', ja);

const getWeatherIcon = (weather) => {
  switch (weather) {
    case 'sunny': return '☀️';
    case 'cloudy': return '☁️';
    case 'rainy': return '☂️';
    default: return '❓';
  }
};

const EnergyGraph = ({ data, targetDate, onDateChange, onViewModeChange }) => {
  const [viewMode, setViewMode] = useState('graph');
  const [highlightedDates, setHighlightedDates] = useState([]);

  useEffect(() => {
    const fetchAvailableDates = async () => {
      try {
        const response = await fetch('/v1/available-dates');
        if (!response.ok) {
          throw new Error('Failed to fetch available dates');
        }
        const dates = await response.json();
        setHighlightedDates(dates.map(d => new Date(d)));
      } catch (error) {
        console.error("Error fetching available dates:", error);
      }
    };
    fetchAvailableDates();
  }, []); // Run only once on component mount

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (onViewModeChange) {
      onViewModeChange(mode);
    }
  };

  const formatTime = (timeStr) => new Date(timeStr).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', hour12: false });

  const getChartData = () => {
    if (data?.time_series) {
      return data.time_series.map(item => ({
        ...item,
        time: formatTime(item.time)
      }));
    }
    return [];
  };

  const chartData = getChartData();
  
  const handleExportCSV = () => {
    if (!chartData || chartData.length === 0) {
      alert("エクスポートするデータがありません。");
      return;
    }
    const formatNumberForCSV = (num) => (typeof num === 'number' ? num.toFixed(1) : '');
    const headers = ['時刻', '天気', '気温[℃]', '日射量[kW/m²]', '降水量[mm]', '消費量予測[kWh]', '発電量予測[kWh]', '買電量[kWh]', '売電量[kWh]', '蓄電量[kWh]', '放電量[kWh]'];
    const headerRow = headers.map(h => `"${h}"`).join(',');
    const dataKeys = ['time', 'weather', 'temperature', 'solarRadiation', 'precipitation', 'consumption', 'generation', 'purchase', 'sale', 'storage', 'discharge'];
    const dataRows = chartData.map(row => {
      return dataKeys.map(key => {
        let value = row[key];
        if (key === 'weather') value = getWeatherIcon(value);
        else if (typeof value === 'number') value = formatNumberForCSV(value);
        else if (value === undefined || value === null) value = '';
        return `"${value}"`;
      }).join(',');
    });
    const csvContent = [headerRow, ...dataRows].join('\n');
    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const fileNameDate = targetDate ? targetDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `電力収支計画_${fileNameDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getYAxisDomain = () => {
    if (!chartData || chartData.length === 0) return [0, 100];
    const dataKeys = ['purchase', 'sale', 'storage', 'discharge', 'consumption', 'generation'];
    let dataMin = Infinity;
    let dataMax = -Infinity;
    chartData.forEach(item => {
      dataKeys.forEach(key => {
        const value = item[key];
        if (value !== undefined && value !== null) {
          dataMin = Math.min(dataMin, value);
          dataMax = Math.max(dataMax, value);
        }
      });
    });
    if (dataMin === Infinity || dataMax === -Infinity) return [0, 100];
    let yMin;
    if (dataMin >= 0) yMin = 0;
    else yMin = Math.floor((dataMin * 1.1) / 10) * 10;
    const yMax = Math.ceil((dataMax * 1.1) / 10) * 10;
    if (yMin === yMax) return [yMin > 0 ? yMin - 10 : yMin -10, yMax + 10];
    return [yMin, yMax];
  };

  const yDomain = getYAxisDomain();

  const getPlanDateDisplay = () => {
    let displayDate;
    let isLatest = false;
    if (targetDate) {
      displayDate = targetDate;
    } else if (data?.time_series && data.time_series.length > 0) {
      displayDate = new Date(data.time_series[0].time);
      isLatest = true;
    } else {
      return null;
    }
    const year = displayDate.getFullYear();
    const month = String(displayDate.getMonth() + 1).padStart(2, '0');
    const day = String(displayDate.getDate()).padStart(2, '0');
    const dateString = `${year}/${month}/${day}`;
    return isLatest ? `${dateString} (最新)` : dateString;
  };

  const planDateDisplay = getPlanDateDisplay();

  const CustomCalendarIcon = React.forwardRef(({ onClick }, ref) => (
    <button className="control-btn calendar-btn" onClick={onClick} ref={ref}>
      📅
    </button>
  ));

  const renderGraph = () => (
    <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="time" interval={2} tick={{ fontSize: 24 }} />
      <YAxis domain={yDomain} tick={{ fontSize: 24 }} label={{ value: 'kWh', angle: -90, position: 'insideLeft' }} />
      <Tooltip />
      <Legend />
      <Bar dataKey="purchase" fill="#28a745" name="買電量[kWh]" barSize={40} />
      <Bar dataKey="sale" fill="#dc3545" name="売電量[kWh]" barSize={40} />
      <Bar dataKey="storage" fill="#6f42c1" name="蓄電量[kWh]" barSize={40} />
      <Bar dataKey="discharge" fill="#ffc107" name="放電量[kWh]" barSize={40} />
      <Line type="monotone" dataKey="consumption" stroke="#ff0000" strokeWidth={3} name="消費量予測[kWh]" dot={true} />
      <Line type="monotone" dataKey="generation" stroke="#0000ff" strokeWidth={3} name="発電量予測[kWh]" dot={true} />
    </ComposedChart>
  );

  return (
    <div className="energy-graph">
      <div className="graph-header">
        <div className="title-container">
          <h2 className="graph-title">EMSダッシュボード</h2>
          <span className="plan-title">電力収支計画</span>
          {planDateDisplay && <span className="plan-date-display">{planDateDisplay}</span>}
          <div className="date-controls">
            <DatePicker 
              selected={targetDate}
              onChange={(date) => onDateChange(date)}
              locale="ja"
              dateFormat="yyyy/MM/dd"
              customInput={<CustomCalendarIcon />}
              maxDate={new Date()}
              highlightDates={highlightedDates}
            />
            <button className="control-btn" onClick={() => onDateChange(null)} disabled={!targetDate}>最新表示</button>
          </div>
        </div>
        <div className="display-controls">
          {viewMode === 'table' && (
            <button className="control-btn export-csv-btn" onClick={handleExportCSV}>
              ⬇️ CSV出力
            </button>
          )}
          <button className={`control-btn ${viewMode === 'graph' ? 'active' : ''}`} onClick={() => handleViewModeChange('graph')}>📊 グラフ</button>
          <button className={`control-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => handleViewModeChange('table')}>📋 表</button>
        </div>
      </div>
      <div className="display-content">
        {viewMode === 'graph' ? (
          <div className="graph-container">
            <ResponsiveContainer width="100%" height="100%">
              {renderGraph()}
            </ResponsiveContainer>
          </div>
        ) : (
          <DataTable data={chartData} />
        )}
      </div>
    </div>
  );
};

export default EnergyGraph;